import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    SchemaEntity,
    SchemaAttribute,
    ProcessedSchema,
    ProcessedSchemaAttribute,
    SchemaEntityResponse,
    SchemaAttributeResponse
} from '../types/SchemaTypes';
import { loadSchemaData } from './schemaThunks';

interface SchemaState {
    // Raw data from API
    schemaAttributes: SchemaAttribute[];
    schemaEntities: SchemaEntity[];
    
    // Processed/combined data
    processedSchemas: ProcessedSchema[];
    fieldOptions: Array<{
        name: string;
        id: string;
        value: string;
        label: string;
        type: string;
        required: boolean;
        multiValued: boolean;
        isReference: boolean;
    }>;
    
    // Loading state
    loading: boolean;
    error: string | null;
    
    // Track if data has been loaded
    isLoaded: boolean;
}

const initialState: SchemaState = {
    schemaAttributes: [],
    schemaEntities: [],
    processedSchemas: [],
    fieldOptions: [],
    loading: false,
    error: null,
    isLoaded: false
};

const schemaSlice = createSlice({
    name: 'schema',
    initialState,
    reducers: {
        clearSchemaData: (state) => {
            state.schemaAttributes = [];
            state.schemaEntities = [];
            state.processedSchemas = [];
            state.fieldOptions = [];
            state.isLoaded = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadSchemaData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loadSchemaData.fulfilled, (state, action) => {
                state.schemaEntities = action.payload.entities;
                state.schemaAttributes = action.payload.attributes;
                state.processedSchemas = action.payload.processedSchemas;
                state.fieldOptions = action.payload.fieldOptions;
                state.loading = false;
                state.error = null;
                state.isLoaded = true;
            })
            .addCase(loadSchemaData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Failed to load schemas';
            });
    }
});

export const { clearSchemaData } = schemaSlice.actions;
export default schemaSlice.reducer;

